import { PageTitle } from 'components/core/PageTitle'
import { useAuth } from 'hooks/useAuth'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { $fetch } from 'utils/fetch'
import DailyCallsBarChart from './CallChart'
import ServerRack from './InfraMap'
import PingGauge from './Ping'
import StaticTable from './static_table'
import AtlassianStyleStatusBar from './statusBar'

const RATE_LIMITS = [
  {
    value: 'start',
    label: 'Start',
    hourly: '100',
    hourly_val: 100,
    daily_val: 100,
    daily: '100',
    concurrent: '10',
    price: 'Default',
    rate_limit_template: 5,
    voice_limit: 0,
  },
  {
    value: 'build',
    label: 'Build',
    daily: '2,000',
    daily_val: 2000,
    hourly: '1,000',
    hourly_val: 1000,
    concurrent: '50',
    price: '$299',
    rate_limit_template: 6,
    voice_limit: 5,
  },
  {
    value: 'scale',
    label: 'Scale',
    daily: '5,000',
    daily_val: 5000,
    hourly_val: 1000,
    hourly: '1,000',
    concurrent: '100',
    price: '$499',
    rate_limit_template: 7,
    voice_limit: 15,
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    daily: 'Unlimited',
    daily_val: 100000,
    hourly_val: 10000,
    hourly: 'Unlimited',
    concurrent: 'Unlimited',
    price: 'Contact Us',
    rate_limit_template: [2, 3],
    voice_limit: 'Unlimited',
  },
]

export function ServerAndLimits() {
  const [user_rate_limit_template, set_user_rate_limit_template]
    = useState(null)
  const { org } = useAuth()
  const [loading, setLoading] = useState(false)
  const [lastSevenCalls, setLastSevenCalls] = useState([])
  const [placementGroup, setPlacementGroup] = useState(null)

  function getMatchingRateLimitTemplate(userRateLimitTemplate) {
    console.log(org?.org_rate_limit, userRateLimitTemplate)
    if (org?.org_type === 'enterprise') {
      return RATE_LIMITS.find(plan => plan.value === 'enterprise')
    }

    return RATE_LIMITS.find((plan) => {
      if (Array.isArray(plan.rate_limit_template)) {
        return plan.rate_limit_template.includes(userRateLimitTemplate)
      }
      return plan.rate_limit_template === userRateLimitTemplate
    })
  }

  useEffect(() => {
    const fetchData = async () => {
      const [callsData] = await Promise.all([
        $fetch(`/analytics/last_seven_calls`, {}, false),
      ])

      // 1a) Process calls for charts
      if (callsData.data) {
        const now = new Date()

        const processedData = callsData.data
          .map(item => ({
            hour: new Date(item.hour),
            count: Number.parseInt(item.count, 10) || 0,
          }))
          .filter(item => item.hour <= now)
          .sort((a, b) => a.hour - b.hour)
        console.log(processedData)
        setLastSevenCalls(processedData)
      }
    }
    fetchData()
  }, [])

  const refreshUserData = async () => {
    set_user_rate_limit_template(org?.org_rate_limit)

    const newPlacementGroup = org?.placement_group
    setPlacementGroup(newPlacementGroup)
  }

  const handleCalculateRateLimitUsage = async () => {
    try {
      setLoading(true)
      const localizeDate = (utcDate) => {
        const date = new Date(utcDate)
        return date.toISOString().slice(0, 10)
      }

      const response = await $fetch(
        `/v1/calls?created_at=${localizeDate(new Date())}&limit=7`,
        {
          method: 'GET',
        },
      )

      if (!user_rate_limit_template)
        return console.log('NO_USER_RL_TEMPLATE_FOUND')
      const template = RATE_LIMITS.find((doc) => {
        if (
          org?.org_type === 'enterprise'
          || user_rate_limit_template === 2
          || user_rate_limit_template === 3
          || (placementGroup !== 'blandshared' && placementGroup !== 'bland')
        ) {
          return doc.value === 'enterprise'
        }
        else {
          return doc.rate_limit_template === user_rate_limit_template
        }
      })
      if (!template)
        return console.log('NO_TEMPLATE_FOUND')

      const dayUsage = Number(
        ((response?.total_count / template.daily_val) * 100).toFixed(2),
      )

      return setUsageData({
        hourUsage: 0,
        dayUsage,
        dayStr: `${response?.total_count} of ${template.daily} Calls`,
        hourStr: '',
      })
    }
    catch (error) {}
  }

  useEffect(() => {
    if (user_rate_limit_template && placementGroup) {
      handleCalculateRateLimitUsage().finally(() => {
        setLoading(false)
      })
    }
  }, [user_rate_limit_template, placementGroup])

  useEffect(() => {
    refreshUserData()
  }, [])

  if (!loading && !user_rate_limit_template)
    return <div />
  if (loading)
    return <div />

  console.log(getMatchingRateLimitTemplate(user_rate_limit_template))

  return (
    <Wrapper style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <PageTitle style={{ fontSize: 16, fontWeight: '550' }}>
        Account Usage
      </PageTitle>

      {/* Optionally, usage progress bar can go here... */}

      {/*
        This container is now forced to at most half the viewport height
        with overflow if needed.
      */}
      <div className="w-full bg-gray-50 rounded border border-gray-200 max-h-[70vh] rounded-xl p-2.5 flex flex-col h-full ">
        <div className="w-full px-2.5 py-2.5 font-semibold">Server Status</div>
        <div className="w-full justify-between">
          <AtlassianStyleStatusBar />

          <div className="grid grid-cols-12 gap-2.5 ">
            {/* Charts Section - 7 columns */}
            <div className="col-span-5">
              <div className="h-full">
                <DailyCallsBarChart
                  lastSevenCalls={lastSevenCalls}
                  dailyRateLimit={
                    getMatchingRateLimitTemplate(user_rate_limit_template)
                      ?.daily_val
                  }
                  width={500}
                  height={250}
                  mockData={false}
                  chartScale={0.8}
                />
              </div>
            </div>

            {/* Ping Gauge - 2 columns */}
            <div className="col-span-2">
              <div className="h-full flex">
                <PingGauge />
              </div>
            </div>

            {/* Server Rack - 3 columns */}
            <div className="col-span-5">
              <div className="h-full flex">
                <ServerRack rate_limit_template={user_rate_limit_template} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <StaticTable />
      </div>

    </Wrapper>
  )
}

// Styled Components
const Wrapper = styled.div`
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`
